import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Loader from '../common/Loader.js';
import Button from '../common/Button.js';
import { Eye, EyeOff, User, Lock, LogIn, ArrowLeft } from 'lucide-react';
import AuthLayout, { authInputClass, authIconClass, authPrimaryBtnClass, authOutlineLinkClass, authEyeBtnClass } from './AuthLayout.js';
import { useTranslation } from 'react-i18next';
import LoginOtpModal from './LoginOtpModal.js';

const ENABLE_LOGIN_OTP = process.env.REACT_APP_ENABLE_LOGIN_OTP === 'true';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpChallenge, setOtpChallenge] = useState(null);
  const [otpError, setOtpError] = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpResending, setOtpResending] = useState(false);

  const navigate = useNavigate();
  const { login, verifyLoginOtp, resendLoginOtp, isAuthenticated, user, loading: authLoading } = useAuth();

  // ✅ FIX 1: Use a ref to prevent the redirect from firing more than once
  const hasRedirected = useRef(false);

  const redirectToDashboard = (role) => {
    // ✅ FIX 2: Guard so redirect only happens once per mount
    if (hasRedirected.current) return;
    hasRedirected.current = true;

    console.log('🔀 [REDIRECT] Role received:', role);
    console.log('🔀 [REDIRECT] hasRedirected flag:', hasRedirected.current);

    switch (role) {
      case 'System Admin':
        navigate('/system-admin-dashboard', { replace: true });
        break;
      case 'Accountant':
        navigate('/accountant-dashboard', { replace: true });
        break;
      case 'Agent':
        navigate('/agent-dashboard', { replace: true });
        break;
      case 'Admin':
        navigate('/admin-dashboard', { replace: true });
        break;
      case 'Hub Admin':
        navigate('/admin-dashboard', { replace: true });
        break;
      case 'Contract Party Admin':
        navigate('/contract-party-dashboard', { replace: true });
        break;
      default:
        navigate('/admin-dashboard', { replace: true });
    }
  };

  // ✅ FIX 3: Only redirect when BOTH isAuthenticated AND user are stable/truthy
  // Also check loading is false so we don't redirect during an in-progress login
  useEffect(() => {
    console.log('👁️ [AUTH EFFECT] authLoading:', authLoading, '| isAuthenticated:', isAuthenticated, '| user:', user, '| loading:', loading);
    // Wait for auth context to finish initializing before redirecting
    if (!authLoading && isAuthenticated && user && user.role && !loading) {
      console.log('👁️ [AUTH EFFECT] All conditions met — redirecting for role:', user.role);
      redirectToDashboard(user.role);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, user, loading]);

  // ✅ FIX 4: Reset the redirect guard when component unmounts (clean-up)
  useEffect(() => {
    return () => {
      hasRedirected.current = false;
    };
  }, []);

  useEffect(() => {
    const savedUsername = localStorage.getItem('savedUsername') || localStorage.getItem('savedEmail');
    const savedPassword = localStorage.getItem('savedPassword');
    const rememberMeStatus = localStorage.getItem('rememberMe') === 'true';

    if (rememberMeStatus && savedUsername && savedPassword) {
      setUsername(savedUsername);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('🚀 [LOGIN] Form submitted');
    console.log('🚀 [LOGIN] Username entered:', username);
    console.log('🚀 [LOGIN] Password length:', password?.length);
    console.log('🚀 [LOGIN] Remember me:', rememberMe);

    try {
      if (rememberMe) {
        localStorage.setItem('savedUsername', username);
        localStorage.removeItem('savedEmail');
        localStorage.setItem('savedPassword', password);
        localStorage.setItem('rememberMe', 'true');
        console.log('💾 [LOGIN] Credentials saved to localStorage');
      } else {
        localStorage.removeItem('savedUsername');
        localStorage.removeItem('savedEmail');
        localStorage.removeItem('savedPassword');
        localStorage.removeItem('rememberMe');
        console.log('🗑️ [LOGIN] Saved credentials cleared from localStorage');
      }

      console.log('📡 [LOGIN] Sending request to /auth/login with username:', username);
      const response = await login(username, password, null, ENABLE_LOGIN_OTP);
      console.log('✅ [LOGIN] Response received:', response);

      if (response?.otp_required) {
        setOtpChallenge(response);
        setOtpError('');
        hasRedirected.current = false;
        return;
      }

      console.log('✅ [LOGIN] User data:', response?.data);
      console.log('✅ [LOGIN] Role:', response?.data?.role);
      console.log('✅ [LOGIN] Token:', response?.data?.token);
      console.log('✅ [LOGIN] Token expiry:', response?.data?.token_expiry);
      console.log('✅ [LOGIN] Company:', response?.data?.company_name);
      console.log('✅ [LOGIN] sessionStorage after login:', sessionStorage.getItem('user'));

      if (response && response.data && response.data.role) {
        console.log('🔀 [LOGIN] Redirecting to dashboard for role:', response.data.role);
        redirectToDashboard(response.data.role);
      } else {
        console.warn('⚠️ [LOGIN] No role found in response — redirect skipped');
      }

    } catch (err) {
      console.error('❌ [LOGIN] Login failed');
      console.error('❌ [LOGIN] Error object:', err);
      console.error('❌ [LOGIN] HTTP status:', err?.response?.status);
      console.error('❌ [LOGIN] Server error message:', err?.response?.data?.error);
      console.error('❌ [LOGIN] Full server response:', err?.response?.data);

      setError(err.response?.data?.error || 'Login failed. Please check your credentials and try again.');
      hasRedirected.current = false;
    } finally {
      setLoading(false);
      console.log('🏁 [LOGIN] Login attempt finished. Loading set to false.');
    }
  };

  const handleVerifyOtp = async (otp) => {
    if (!otpChallenge?.challenge_id) return;
    setOtpError('');
    setOtpVerifying(true);
    try {
      const response = await verifyLoginOtp(otpChallenge.challenge_id, otp);
      setOtpChallenge(null);
      if (response && response.data && response.data.role) {
        redirectToDashboard(response.data.role);
      }
    } catch (err) {
      setOtpError(err.response?.data?.error || 'Invalid OTP. Please try again.');
      hasRedirected.current = false;
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (!otpChallenge?.challenge_id) return;
    setOtpError('');
    setOtpResending(true);
    try {
      const response = await resendLoginOtp(otpChallenge.challenge_id);
      setOtpChallenge((prev) => ({
        ...(prev || {}),
        ...response,
        challenge_id: response.challenge_id || prev?.challenge_id,
      }));
    } catch (err) {
      setOtpError(err.response?.data?.error || 'Failed to resend OTP. Please try again.');
    } finally {
      setOtpResending(false);
    }
  };

  return (
    <AuthLayout title="FOR PARTNERS">
      {error && (
        <div className="mb-5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <User className={authIconClass} strokeWidth={1.75} />
          <input
            type="text"
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className={`${authInputClass} pl-12 pr-4`}
            required
            disabled={loading}
          />
        </div>

        <div className="relative">
          <Lock className={authIconClass} strokeWidth={1.75} />
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`${authInputClass} pl-12 pr-12`}
            required
            disabled={loading}
          />
          <Button
            variant="ghost"
            size="sm"
            icon={showPassword ? EyeOff : Eye}
            onClick={() => setShowPassword(!showPassword)}
            className={authEyeBtnClass}
          />
        </div>

        <div className="flex items-center justify-between gap-3 text-[13px] pt-1">
          <label className="flex items-center gap-2 text-slate-600 cursor-pointer select-none min-h-[32px]">
            <input
              type="checkbox"
              className="rounded cursor-pointer"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={loading}
            />
            Remember me
          </label>

          <Link
            to="/forgot-password"
            className="font-semibold text-blue-600 hover:text-blue-800 transition py-1.5"
          >
            Forgot password
          </Link>
        </div>

        <Button
          variant="primary"
          type="submit"
          fullWidth
          loading={loading}
          size="lg"
          icon={LogIn}
          className={authPrimaryBtnClass}
        >
          {loading ? 'SIGNING IN...' : 'SIGN IN'}
        </Button>

        <div className="pt-1">
          <Link to="/" className={authOutlineLinkClass}>
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        <p className="pt-2 text-[11px] leading-relaxed text-slate-400 text-center">
          By clicking on Sign In, I accept the{' '}
          <span className="text-blue-600">Terms &amp; Conditions</span> and{' '}
          <span className="text-blue-600">Privacy Policy</span>.
        </p>
      </form>

      <LoginOtpModal
        open={Boolean(otpChallenge)}
        challenge={otpChallenge}
        verifying={otpVerifying}
        resending={otpResending}
        error={otpError}
        onClose={() => {
          if (otpVerifying) return;
          setOtpChallenge(null);
          setOtpError('');
        }}
        onVerify={handleVerifyOtp}
        onResend={handleResendOtp}
      />
    </AuthLayout>
  );
};

export default Login;